import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { fetchPaymentStatus } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { CheckCircle, XCircle, ArrowsClockwise, ArrowRight } from "@phosphor-icons/react";

export default function PaymentSuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [status, setStatus] = useState("polling");
  const [data, setData] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus("invalid");
      return;
    }
    let cancelled = false;
    const poll = async () => {
      attempts.current += 1;
      try {
        const r = await fetchPaymentStatus(sessionId);
        if (cancelled) return;
        setData(r);
        if (r.payment_status === "paid") {
          setStatus("paid");
          refreshUser();
          return;
        }
        if (r.status === "expired") {
          setStatus("expired");
          return;
        }
        if (attempts.current >= 8) {
          setStatus("timeout");
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        if (!cancelled) setStatus("error");
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId, refreshUser]);

  return (
    <div className="max-w-2xl mx-auto px-4 md:px-8 py-20 text-center" data-testid="payment-success-page">
      {status === "polling" && (
        <>
          <ArrowsClockwise size={48} className="animate-spin text-blue-600 mx-auto mb-6" />
          <h1 className="font-bold text-3xl mb-3 text-slate-900" data-testid="payment-status-polling">Confirming your payment…</h1>
          <p className="text-slate-500">Hang tight, we're verifying with Stripe.</p>
        </>
      )}
      {status === "paid" && (
        <>
          <CheckCircle size={56} weight="fill" className="text-green-500 mx-auto mb-6" />
          <h1 className="font-bold text-4xl mb-3 text-slate-900" data-testid="payment-status-paid">Welcome to Premium.</h1>
          <p className="text-slate-500 mb-8">
            Your subscription is active and daily limits are removed.
          </p>
          <div className="flex gap-3 justify-center">
            <Link to="/" className="bg-blue-600 hover:bg-blue-700 text-white font-bold px-6 py-3 text-sm rounded-lg inline-flex items-center gap-2 transition-colors">
              Back to desk <ArrowRight size={14} />
            </Link>
            <Link to="/profile" className="border border-slate-200 hover:bg-slate-50 text-slate-700 px-6 py-3 text-sm rounded-lg transition-colors">
              View subscription
            </Link>
          </div>
        </>
      )}
      {["expired", "timeout", "error", "invalid"].includes(status) && (
        <>
          <XCircle size={56} weight="fill" className="text-slate-400 mx-auto mb-6" />
          <h1 className="font-bold text-3xl mb-3 text-slate-900" data-testid="payment-status-error">Payment not confirmed</h1>
          <p className="text-slate-500 mb-8">
            {status === "expired" && "The checkout session expired."}
            {status === "timeout" && "We couldn't confirm the payment in time — check back in a few minutes."}
            {status === "error" && "Something went wrong while verifying the payment."}
            {status === "invalid" && "Missing session reference."}
          </p>
          <Link to="/pricing" className="border border-slate-200 hover:bg-slate-50 text-slate-700 px-6 py-3 text-sm rounded-lg transition-colors">
            Back to pricing
          </Link>
        </>
      )}
    </div>
  );
}
